class CartDrawer {
  constructor() {
    this.activeTrigger = null;
    this.isOpen = false;

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleDocumentSubmit = this.handleDocumentSubmit.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleOpenEvent = this.handleOpenEvent.bind(this);

    document.addEventListener('click', this.handleDocumentClick);
    document.addEventListener('submit', this.handleDocumentSubmit, true);
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('cart-drawer:open', this.handleOpenEvent);

    this.registerYmqBridge();
  }

  getRoot() {
    return document.querySelector('[data-cart-drawer-root]');
  }

  getPanel() {
    const root = this.getRoot();
    return root ? root.querySelector('[data-cart-drawer-panel]') : null;
  }

  handleOpenEvent() {
    this.open({ refresh: true });
  }

  registerYmqBridge() {
    const drawer = this;
    const ymqCart = window.ymqCart || {};
    const slid = ymqCart.slid || {};

    ymqCart.slid = slid;
    ymqCart.slidOpen = function () {
      slid.activeId = 'cart-drawer';
      window.dispatchEvent(new CustomEvent('cart-drawer:open'));
      return true;
    };

    window.ymqCart = ymqCart;
  }

  handleDocumentClick(event) {
    const headerCartButton = event.target.closest('.site-header-cart-button');

    if (headerCartButton) {
      event.preventDefault();
      this.activeTrigger = headerCartButton;
      this.open({ refresh: true });
      return;
    }

    if (event.target.closest('[data-cart-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    const removeButton = event.target.closest('[data-cart-drawer-remove]');

    if (removeButton) {
      event.preventDefault();
      this.removeItem(removeButton);
      return;
    }

    const recommendationButton = event.target.closest('[data-cart-drawer-recommendation-add]');

    if (recommendationButton) {
      event.preventDefault();
      this.addRecommendation(recommendationButton);
    }
  }

  handleKeydown(event) {
    if (event.key === 'Escape' && this.isOpen) {
      this.close();
    }
  }

  handleDocumentSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) {
      return;
    }

    if (!this.isYmqProductForm(form)) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    this.submitYmqProductForm(form, event.submitter);
  }

  async open({ refresh = false } = {}) {
    if (refresh) {
      await this.refresh();
    }

    const root = this.getRoot();
    const panel = this.getPanel();

    if (!root || !panel) {
      return;
    }

    this.isOpen = true;
    if (window.ymqCart && window.ymqCart.slid) {
      window.ymqCart.slid.activeId = 'cart-drawer';
    }
    root.classList.add('is-visible');
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-visible');
    panel.focus();

    this.loadRecommendations();
  }

  close() {
    const root = this.getRoot();

    if (!root) {
      return;
    }

    this.isOpen = false;
    if (window.ymqCart && window.ymqCart.slid) {
      window.ymqCart.slid.activeId = null;
    }
    root.classList.remove('is-visible');
    root.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-visible');

    if (this.activeTrigger) {
      this.activeTrigger.focus();
    }
  }

  async refresh() {
    const wasOpen = this.isOpen;
    const nextRoot = await this.fetchSection(window.Theme.routes.root_url, 'cart-drawer');

    if (!nextRoot) {
      return;
    }

    const currentRoot = this.getRoot();

    if (currentRoot) {
      currentRoot.replaceWith(nextRoot);
    } else {
      document.body.appendChild(nextRoot);
    }

    this.updateHeaderCount(Number(nextRoot.dataset.cartCount || 0));

    if (wasOpen) {
      nextRoot.classList.add('is-visible');
      nextRoot.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-visible');
    }
  }

  async loadRecommendations() {
    const root = this.getRoot();

    if (!root) {
      return;
    }

    const container = root.querySelector('[data-cart-drawer-recommendations]');

    if (!container) {
      return;
    }

    const productId = container.dataset.productId;

    if (!productId) {
      container.innerHTML = '';
      return;
    }

    const url = new URL(window.Theme.routes.product_recommendations_url, window.location.origin);
    url.searchParams.set('section_id', 'cart-drawer-recommendations');
    url.searchParams.set('limit', '1');
    url.searchParams.set('product_id', productId);

    try {
      const response = await fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Unable to load recommendations');
      }

      container.innerHTML = await response.text();
    } catch (error) {
      container.innerHTML = '';
    }
  }

  async addRecommendation(button) {
    const variantId = button.dataset.variantId;

    if (!variantId) {
      return;
    }

    const previousText = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding...';

    try {
      const response = await fetch(`${window.Theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          id: variantId,
          quantity: '1'
        }).toString()
      });

      if (!response.ok) {
        throw new Error('Unable to add item');
      }

      if (window.api && window.api.theme) {
        window.api.theme.triggerEvent('cart:updated', {
          cancel: () => {}
        });
      }

      await this.syncState();
    } catch (error) {
      button.disabled = false;
      button.textContent = previousText;
    }
  }

  async removeItem(button) {
    const line = button.dataset.line;

    if (!line) {
      return;
    }

    const previousContent = button.innerHTML;
    button.disabled = true;
    button.innerHTML = '<span>Removing...</span>';

    try {
      const response = await fetch(`${window.Theme.routes.cart_change_url}.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: new URLSearchParams({
          line,
          quantity: '0'
        }).toString()
      });

      if (!response.ok) {
        throw new Error('Unable to remove item');
      }

      if (window.api && window.api.theme) {
        window.api.theme.triggerEvent('cart:updated', {
          cancel: () => {}
        });
      }

      await this.syncState();
    } catch (error) {
      button.disabled = false;
      button.innerHTML = previousContent;
    }
  }

  async fetchSection(basePath, sectionId) {
    try {
      const url = new URL(basePath, window.location.origin);
      url.searchParams.set('section_id', sectionId);

      const response = await fetch(url.toString(), {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        return null;
      }

      const markup = await response.text();
      const template = document.createElement('template');
      template.innerHTML = markup.trim();

      return template.content.querySelector('[data-cart-drawer-root]');
    } catch (error) {
      return null;
    }
  }

  updateHeaderCount(itemCount) {
    const normalizedCount = itemCount > 0 ? String(itemCount) : '';

    document.querySelectorAll('[data-header-cart-count]').forEach((countBubble) => {
      countBubble.setAttribute('data-header-cart-count', normalizedCount);
      countBubble.classList.toggle('visible', itemCount > 0);
    });

    document.querySelectorAll('.site-header-cart-button').forEach((cartButton) => {
      cartButton.classList.toggle('count-hidden', itemCount === 0);
    });
  }

  async syncState() {
    await this.refresh();

    if (window.api && window.api.theme) {
      const cartResponse = await fetch(`${window.Theme.routes.cart_url}.js`, {
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (cartResponse.ok) {
        const cart = await cartResponse.json();

        window.api.theme.updateValue('cart:total', {
          price: cart.total_price,
          compare_at_price: cart.total_price
        });
        window.api.theme.updateValue('cart:checkout', cart.item_count > 0);
        window.api.theme.updateElement('cart:notifications');
        window.api.theme.updateElement('cart:tools');
      }
    }

    await this.open();
  }

  isYmqProductForm(form) {
    if (!form.matches('[data-product-form]')) {
      return false;
    }

    if (!form.closest('[data-product-wrapper]')) {
      return false;
    }

    const ymqOption = window.ymq_option;

    if (!ymqOption || ymqOption.page !== 'product') {
      return false;
    }

    if (eventualObjectSize(ymqOption.ymq_product_options) > 0) {
      return true;
    }

    return eventualObjectSize(ymqOption.ymq_option_data) > 0
      || eventualObjectSize(ymqOption.ymq_status) > 0
      || eventualObjectSize(ymqOption.ymq_variantjson) > 0
      || eventualObjectSize(ymqOption.ymq_template_options) > 0;
  }

  async submitYmqProductForm(form, submitter) {
    const atcButton = submitter && submitter.matches('[data-product-atc]')
      ? submitter
      : form.querySelector('[data-product-atc]');
    const formData = new FormData(form);

    if (atcButton) {
      atcButton.disabled = true;
      atcButton.classList.add('processing');
    }

    try {
      const response = await fetch(`${window.Theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Unable to add item');
      }

      if (window.api && window.api.theme) {
        window.api.theme.triggerEvent('cart:updated', {
          cancel: () => {}
        });
      }

      await this.syncState();
    } catch (error) {
    } finally {
      if (atcButton) {
        atcButton.disabled = false;
        atcButton.classList.remove('processing');
      }
    }
  }
}

function eventualObjectSize(value) {
  if (!value || typeof value !== 'object') {
    return 0;
  }

  return Object.keys(value).length;
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawer();
});
